import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';


import HeroSection from './components/HeroSection';
import RegistrationPanel from './components/RegistrationPanel';
import StatsSection from './components/StatsSection';
import FairInfoSection from './components/FairInfoSection';
import BenefitsSection from './components/BenefitsSection';
import HowItWorks from './components/HowItWorks';
import UpcomingEvents from './components/UpcomingEvents';
import ExhibitionHallPreview from './components/ExhibitionHallPreview';

const StudyExpoHomePage = () => {
  const navigate = useNavigate();
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation Header */}
      <nav className="fixed top-0 left-0 right-0 h-[64px] bg-white/90 backdrop-blur-md shadow-sm border-b border-border z-50">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 h-full flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-9 h-9 bg-gradient-to-br from-cyan-500 to-violet-600 rounded-xl flex items-center justify-center shadow-md">
              <Icon name="GraduationCap" size={20} color="white" />
            </div>
            <span className="text-xl font-heading font-bold text-foreground">StudyExpo</span>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => navigate('/institute-login')}
              className="px-4 py-2 text-sm font-semibold text-violet-700 border border-violet-200 rounded-lg hover:bg-violet-50 transition-all"
            >
              Institute Login
            </button>
            <button
              onClick={() => navigate('/student-registration')}
              className="px-5 py-2 text-sm font-semibold bg-gradient-to-r from-cyan-600 to-violet-600 text-white rounded-lg shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
            >
              Get Started Free
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-muted transition-all"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <Icon name={mobileMenuOpen ? 'X' : 'Menu'} size={22} />
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-[64px] left-0 right-0 bg-white border-b border-border shadow-lg p-4 space-y-2 z-50">
            <button onClick={() => { navigate('/institute-login'); setMobileMenuOpen(false); }} className="w-full text-left px-4 py-3 text-sm font-semibold text-violet-700 bg-violet-50 rounded-lg transition-all">
              Institute Login
            </button>
            <button onClick={() => { navigate('/student-registration'); setMobileMenuOpen(false); }} className="w-full text-left px-4 py-3 text-sm font-semibold text-white bg-gradient-to-r from-cyan-600 to-violet-600 rounded-lg transition-all">
              Get Started Free
            </button>
          </div>
        )}
      </nav>
      {/* Main Content */}
      <div className="pt-[64px]">
        {/* Hero Section with Registration Panel */}
        <section className="relative bg-gradient-to-br from-cyan-50/80 via-white to-violet-50/80 py-12 md:py-20 overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-violet-50/60 to-transparent pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-amber-100/40 rounded-full blur-3xl pointer-events-none" />

          <div className="relative max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
              {/* Hero Content - Left Side (2 columns) */}
              <div className="lg:col-span-2">
                <HeroSection />
              </div>

              {/* Registration Panel - Right Side (1 column) */}
              <div className="lg:col-span-1">
                <RegistrationPanel
                  showLoginForm={showLoginForm}
                  setShowLoginForm={setShowLoginForm}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <StatsSection />

        {/* How It Works */}
        <HowItWorks />

        {/* Exhibition Hall Preview — public, no auth required */}
        <ExhibitionHallPreview />

        {/* Fair Information / Features Section */}
        <FairInfoSection />

        {/* Upcoming Events */}
        <UpcomingEvents />

        {/* Benefits Section */}
        <BenefitsSection />

        {/* Footer */}
        <footer className="bg-foreground text-white">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">
              {/* Brand */}
              <div className="md:col-span-2">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 bg-gradient-to-br from-cyan-400 to-violet-500 rounded-xl flex items-center justify-center">
                    <Icon name="GraduationCap" size={20} color="white" />
                  </div>
                  <span className="text-xl font-heading font-bold">StudyExpo</span>
                </div>
                <p className="text-white/60 text-sm leading-relaxed max-w-xs">
                  India's premier virtual education fair platform. Connecting students with top institutions since 2024.
                </p>
                <div className="flex gap-3 mt-5">
                  {['Twitter', 'Linkedin', 'Instagram', 'Youtube']?.map((social) => (
                    <div key={social} className="w-9 h-9 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center cursor-pointer transition-all">
                      <Icon name={social} size={16} color="white" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Platform</h4>
                <ul className="space-y-2.5">
                  {[
                    { label: 'Student Registration', path: '/student-registration' },
                    { label: 'Institute Login', path: '/institute-login' },
                  ]?.map((link) => (
                    <li key={link?.label}>
                      <button
                        onClick={() => navigate(link?.path)}
                        className="text-sm text-white/60 hover:text-white transition-colors"
                      >
                        {link?.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Contact */}
              <div>
                <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Contact</h4>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-sm text-white/60">
                    <Icon name="Mail" size={15} color="currentColor" />
                    <span>info@studyexpo.in</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm text-white/60">
                    <Icon name="Phone" size={15} color="currentColor" />
                    <span>+91 9445455505</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm text-white/60">
                    <Icon name="MapPin" size={15} color="currentColor" />
                    <span>Chennai, Tamil Nadu, India</span>
                  </li>
                </ul>

                <div className="mt-5 p-3 bg-white/10 rounded-xl border border-white/10">
                  <p className="text-xs text-white/50 mb-1">Support Hours</p>
                  <p className="text-sm text-white font-medium">Mon–Sat, 9 AM – 6 PM IST</p>
                </div>
              </div>
            </div>

            {/* Bottom bar */}
            <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-sm text-white/40">&copy; 2026 StudyExpo. All rights reserved.</p>
              <div className="flex gap-6">
                <span className="text-sm text-white/40 hover:text-white/70 cursor-pointer transition-colors">Privacy Policy</span>
                <span className="text-sm text-white/40 hover:text-white/70 cursor-pointer transition-colors">Terms of Service</span>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default StudyExpoHomePage;